import React, {
  PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import Button from '../../../../library/button/button';
import PlpBackToTopButton from '../plp-back-to-top-button/plp-back-to-top-button';

import { useScrollTo } from '../../../../shared/hooks/hooks';

import { ProductData } from '../../../../shared/types/graphql-types';
import {
  PACKAGE_PAGE_SIZE,
  PAGE_SIZE,
  TREADWELL_PRODUCTS_DISPLAY_TYPES
} from '../../../../shared/constants/products';

import './load-more.scss';

interface ProductAndPageNum {
  pageNum: number;
  product: ProductData;
}

interface LoadMoreProps {
  comparePreviewRef: RefObject<HTMLDivElement>;
  isLoading: boolean;
  isPrebuiltPackageSearch: boolean;
  numberOfPages: number;
  pageNum: number;
  products: ProductAndPageNum[];
  setPageNum: (pageNum) => void;
  setTreadwellProductsDisplayType: (type) => void;
  totalNumberOfResults: number;
  treadwellProductsDisplayType: string;
}

function LoadMore(props: PropsWithChildren<LoadMoreProps>) {
  const {
    children,
    comparePreviewRef,
    isLoading,
    isPrebuiltPackageSearch,
    numberOfPages,
    pageNum,
    products,
    setPageNum,
    setTreadwellProductsDisplayType,
    totalNumberOfResults,
    treadwellProductsDisplayType
  } = props;

  const pageSize = isPrebuiltPackageSearch ? PACKAGE_PAGE_SIZE : PAGE_SIZE;
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [shouldLazyLoad, setShouldLazyLoad] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const lazyLoadRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const endPageNumRef = useRef<number>();
  const prevTotalNumberOfResultsRef = useRef<number | null>();
  const productCodeRef = useRef<string | null>();
  const isMoreProducts =
    treadwellProductsDisplayType === TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_THREE
      ? false
      : (endPageNumRef.current || pageNum) < numberOfPages - 1;
  const trueAllProductsLength = isMoreProducts
    ? ((endPageNumRef.current || pageNum) + 1) * pageSize
    : totalNumberOfResults;
  const isPrevProducts =
    treadwellProductsDisplayType === TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_THREE
      ? false
      : totalNumberOfResults > pageSize &&
        products.length < trueAllProductsLength;
  const scrollToCurrProduct = useScrollTo(
    `#product-code-${productCodeRef.current}`,
    {
      behavior: 'auto',
      offset: 300
    }
  );

  const addMoreProducts = useCallback(
    (isPrevClicked?: boolean) => {
      if (
        !isLoading &&
        (endPageNumRef.current || endPageNumRef.current === 0)
      ) {
        if (isPrevClicked) {
          productCodeRef.current = products[0].product.code;
          const prevProductsNum = trueAllProductsLength - products.length;
          const prevPage = prevProductsNum / pageSize - 1;
          setTreadwellProductsDisplayType(
            TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_ALL
          );
          setPageNum(prevPage);
        } else if (isMoreProducts) {
          productCodeRef.current = products[products.length - 1].product.code;
          endPageNumRef.current = endPageNumRef.current + 1;
          setPageNum(endPageNumRef.current);
        }
      }
    },
    [
      isLoading,
      isMoreProducts,
      pageSize,
      products,
      setPageNum,
      setTreadwellProductsDisplayType,
      trueAllProductsLength
    ]
  );

  useEffect(() => {
    if (!isLoading && productCodeRef.current) {
      scrollToCurrProduct();
    }
  }, [isLoading, scrollToCurrProduct]);

  useEffect(() => {
    const lazyLoadObserver = new IntersectionObserver(([entry]) =>
      setIsIntersecting(entry.isIntersecting)
    );

    lazyLoadObserver.observe(lazyLoadRef.current as Element);
    return () => {
      lazyLoadObserver.disconnect();
    };
  }, [lazyLoadRef]);

  useEffect(() => {
    if (
      !isLoading &&
      (endPageNumRef.current === 0 ||
        (endPageNumRef.current || pageNum) % 2 === 0)
    ) {
      timerRef.current = setTimeout(() => {
        setShouldLazyLoad(true);
      }, 1000);
    }

    return () => clearTimeout(timerRef.current);
  }, [isLoading, pageNum]);

  useEffect(() => {
    if (shouldLazyLoad && isIntersecting) {
      addMoreProducts();
      setShouldLazyLoad(false);
    }
  }, [addMoreProducts, isIntersecting, pageNum, shouldLazyLoad]);

  useEffect(() => {
    if (
      endPageNumRef.current === undefined ||
      prevTotalNumberOfResultsRef.current === undefined ||
      totalNumberOfResults !== prevTotalNumberOfResultsRef.current
    ) {
      endPageNumRef.current = pageNum;
      prevTotalNumberOfResultsRef.current = totalNumberOfResults;
      productCodeRef.current = undefined;
    }
  }, [pageNum, totalNumberOfResults]);

  return (
    <div ref={containerRef}>
      {!isLoading && isPrevProducts && (
        <div styleName="top-btn-container">
          <Button
            isDisabled={isLoading}
            isSecondary
            onClick={() => addMoreProducts(true)}
            styleName="load-more-btn"
          >
            Load Previous
          </Button>
        </div>
      )}

      {children}
      <PlpBackToTopButton
        bottomDisplaceRef={comparePreviewRef}
        containerRef={containerRef}
      />

      <div ref={lazyLoadRef}>
        {!isLoading && !shouldLazyLoad && isMoreProducts && (
          <div styleName="bottom-btn-container">
            <Button
              isDisabled={isLoading}
              isSecondary
              onClick={() => addMoreProducts()}
              styleName="load-more-btn"
            >
              Load More
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default LoadMore;
